import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as afSelectors from 'data/accessFunction/accessFunction.selectors';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import * as afFilterActions from 'data/afFilter/afFilter.actions';
import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
import * as afActions from 'data/accessFunction/accessFunction.actions';
import { adaptToCreate } from 'data/afFilter/afFilter.adapter';
import { AfFilter, AfFilterGridData } from 'data/afFilter/afFilter.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newAfFilter } from 'routes/AfFilter/AfFilter.types';
import { afFilterTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptAfFilterTemplate,
  adaptInitialAfFilterFormData,
  adaptAfFilterCreateTemplate,
} from '../../utils/afFilterTemplate.adapter';
import { getAuditDetailsToSubmit } from '../../utils/afFilterTemplateDataToSubmit';
import { Ref } from '../../components/AfFilterForm/AfFilterForm';
import AfFilterForm from '../../components/AfFilterForm';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  afFilterTemplateDef: adaptTemplateVisibilityMode(
    afFilterTemplate,
    'create',
  ),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  afs: afSelectors.getAccessFunctions(state),
  filterGroups: filterGroupSelectors.getFilterGroupList(state),
  afFilterTemplate: adaptTemplateVisibilityMode(afFilterTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
});

const mapDispatch = {
  createAfFilter: afFilterActions.createAfFilter,
  goToAfFilterList:
    afFilterActions.goToAfFilterList,
  getAccessFunctionList: afActions.getAllAFs,
  getFilterGroupList: filterGroupActions.getFilterGroupList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const AfFilterCreate = ({
  history,
  userId,
  deliveryFunctionId,
  goToAfFilterList,
  createAfFilter,
  userName,
  isAuditEnabled,
  afFilterTemplateDef,
  getAccessFunctionList,
  getFilterGroupList,
  afs,
  filterGroups
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<AfFilter>(adaptInitialAfFilterFormData(cloneDeep(newAfFilter)));
  const [template, setTemplate] = useState(afFilterTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getAccessFunctionList(deliveryFunctionId);
    getFilterGroupList();
  }, [
    deliveryFunctionId,
    getAccessFunctionList,
    getFilterGroupList
  ])
  useEffect(() => {
    const adaptedTemplate = adaptAfFilterCreateTemplate(template, afs, filterGroups);
    setTemplate(adaptedTemplate);
  }, [afs, filterGroups])

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as AfFilter);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptAfFilterTemplate(clonedTemplate);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateTLSActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();

    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(
        distributorConfigFormData,
        null,
        isAuditEnabled,
        userId,
        userName,
        'CREATE',
      ),
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateTLSActionPayload();
    if (payload) {
      createAfFilter(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToAfFilterList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <AfFilterForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('afFilter.create.title'))}
        {renderForm()}
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(AfFilterCreate));
